import React, {useEffect, useMemo, useState} from 'react';
import DashboardLayout from "../../../layouts/DashboardLayout";
import PageHeader from "../../../components/PageHeader";
import TextInput from "../../../components/TextInput";
import ProductFormItem from "../../../components/ProductFormItem";
import {PlusIcon} from "@heroicons/react/24/outline";
import Button from "../../../components/Button";
import Toggle from "../../../components/Toggle";
import SelectInput from "../../../components/SelectInput";
import usePaymentFormStore from "../../../store/payment-form";
import {useMutation, useQuery, useQueryClient} from "react-query";
import {useNavigate} from "react-router-dom";
import {apollo} from "../../../utils/apollo";
import {CREATE_PAYMENT, GET_CUSTOMER_LIST, GET_MERCHANT_SUPPLIERS_LIST} from "../../../utils/graphql";
import {generateRandomString} from "../../../utils/helpers";
import {useDeepMemo} from "@apollo/client/react/hooks/internal";
import useAuthStore from "../../../store/auth";
import {useTranslation} from "react-i18next";
import MoneyHighlight from "../../../components/MoneyHighlight";
import {toast} from "react-toastify";
import TestMode from "../../../components/TestMode";
import {READONLY_MERCHANT} from "../../../utils/env";

const CURRENT_DAY = `0${new Date().getDate()}`.slice(-2);
const CURRENT_MONTH = `0${new Date().getMonth() + 1}`.slice(-2);
const CURRENT_YEAR = new Date().getFullYear().toString();

function PaymentCreate() {
    const [date, setDate] = useState({day: CURRENT_DAY, month: CURRENT_MONTH, year: CURRENT_YEAR});
    const {user, merchant} = useAuthStore();
    const {
        useExistingCustomer,
        saveCustomerDetails,
        splitPayment,
        customerUuid,
        name,
        email,
        phone,
        products,
        supplier,
        setUseExistingCustomer,
        setSaveCustomerDetails,
        setSplitPayment,
        setCustomerUuid,
        setName,
        setEmail,
        setPhone,
        setProducts,
        resetPaymentFormAction,
        fillCustomerDetails,
        address,
        setAddress,
        city,
        setCity,
        zipCode,
        setZipCode,
        country,
        customerReference,
        setCountry,
        setCustomerReference,
        expiresAt,
        setExpiresAt,
        setSupplier,
        brokerReference,
        businessLine,
        brokerAmount,
        setBrokerReference,
        setBusinessLine,
        setBrokerAmount,
    } = usePaymentFormStore();
    const queryClient = useQueryClient()
    const navigate = useNavigate();
    const {t} = useTranslation();

    const createPaymentMutation = useMutation({
        mutationFn: async () => {
            /* methaq prod M135728300300999 */
            /* samir dev,prod M862248712438901, M297763427187004 */
            if (['M135728300300999', 'M862248712438901', 'M297763427187004'].includes(merchant?.merchantUuid) && !businessLine) {
                throw new Error(t('payment.create.business_line_required'));
            }
            const supplierVar = merchant?.allowSubVendors === true ? supplier : null;
            const {data} = await apollo.mutate({
                mutation: CREATE_PAYMENT, variables: {
                    input: {
                        useExistingCustomer, splitPayment, customerUuid, name, email, phone, products,
                        address, city, zipCode, country, expiresAt, supplier: supplierVar,
                        saveCustomerDetails: !useExistingCustomer, customerReference,
                        brokerReference, businessLine, brokerAmount
                    }
                },
            });
            return data?.createPayment;
        }, onMutate: () => {
            toast.info('Loading...', {autoClose: false, toastId: 'create-payment-loading'});
        }, onSuccess: (data) => {
            toast.dismiss('create-payment-loading');
            toast.success(t('payment.create.success_message') || 'Form has been submitted successfully!');
            navigate(`/merchant/payments/view/${data?.paymentUuid}`);
            // queryClient.invalidateQueries(['payment_links']).then(() => {
            //     resetPaymentFormAction();
            //     setTimeout(() => navigate("/merchant/payments"), 2000);
            // });
        }, onError: (error) => {
            toast.dismiss('create-payment-loading');
            const message = error?.response?.data?.message || error?.message || t('payment.create.error_message') || 'Error occurred while processing your request. Please, try again.';
            toast.error(message);
        }
    })

    const fetchCustomersList = useQuery({
        // queryKey: 'customers', queryFn: () => http.get("/merchant/customers"),
        queryKey: 'customers', queryFn: async () => {
            const {data} = await apollo.query({
                query: GET_CUSTOMER_LIST, variables: {},
            });
            return data?.getCustomerList;
        }, keepPreviousData: false
    })

    const fetchMerchantSuppliersList = useQuery({
        queryKey: 'merchant_suppliers_list',
        queryFn: async () => {
            const {data} = await apollo.query({
                query: GET_MERCHANT_SUPPLIERS_LIST,
                variables: {},
            });
            return data?.getMerchantSuppliersList;
        },
        keepPreviousData: false
    });

    const customersList = useMemo(() => fetchCustomersList?.isSuccess ? fetchCustomersList?.data?.data : null, [fetchCustomersList]);
    const suppliersList = fetchMerchantSuppliersList?.isSuccess ? fetchMerchantSuppliersList?.data : null;

    useEffect(() => {
        if (useExistingCustomer && customerUuid && customersList) {
            const customer = customersList.find((customer) => customer.customerUuid === customerUuid);
            fillCustomerDetails(customer)
        }
    }, [useExistingCustomer, customerUuid, customersList]);

    const totalAmount = useMemo(() => {
        return products.reduce((total, product) => total + Number(product.total), 0) || 0;
    }, [products]);

    const updateProduct = (id, key, rawValue) => {
        // console.log({id, key, value})
        const updatedProducts = products.map((p) => {
            if (p.id === id) {
                let quantity = Number(p.quantity || 1);
                let amount = Number(p.amount || 1);
                let total = Number(p.total || 1);
                let vat = Number(p.vat || 0);

                let value = Number(rawValue || 0);

                switch (key) {
                    case 'quantity':
                        total = (amount * value) * (1 + (vat / 100));
                        rawValue = value;
                        break;
                    case 'amount':
                        total = (value * quantity) * (1 + (vat / 100));
                        rawValue = value;
                        break;
                    case 'total':
                        amount = value / (quantity * (1 + (vat / 100)));
                        rawValue = value;
                        break;
                    case 'vat':
                        total = (amount * quantity) * (1 + (value / 100));
                        rawValue = value;
                        break;
                    default:
                        break;
                }

                return {
                    ...p,
                    amount: Number(amount.toFixed(2)),
                    vat: Number(vat.toFixed(2)),
                    total: Number(total.toFixed(2)),
                    quantity: Number(quantity.toFixed(2)),
                    [key]: rawValue
                };
            }
            return p;
        });
        setProducts(updatedProducts);
    }

    const deleteProduct = (id) => {
        const updatedProducts = products.filter((p) => p.id !== id);
        setProducts(updatedProducts);
    }

    const addProduct = () => {
        setProducts([...products, {
            id: generateRandomString(),
            title: "",
            details: "",
            amount: 0,
            vat: 0,
            quantity: 1,
            total: 0
        }]);
    }

    const selectedDateFormated = useDeepMemo(() => {
        try {
            return new Date(`${date.year}-${date.month}-${date.day}`)
        } catch (e) {
            return null;
        }
    }, [date]);

    return (<DashboardLayout>
        <PageHeader back="/merchant/payments" title={t('payment.create.title')}
                    description={t('payment.create.description')}>
            <TestMode hidden={READONLY_MERCHANT}/>
        </PageHeader>

        <div className="mt-8 flex items-center gap-6">
            <Toggle get={useExistingCustomer}
                    set={value => setUseExistingCustomer(value)}>{t('payment.create.use_existing_customer')}</Toggle>
        </div>

        <div className="mt-8 bg-white rounded-xl border border-gray-200 shadow-sm mb-6">
            <div className="flex items-center justify-between gap-4">
                <h3 className="w-full text-lg font-medium text-white px-6 py-4 bg-gradient-to-br from-[#4296b5] to-[#95e0f2] rounded-t-xl -mt-6 border-b border-gray-200">{t('payment.create.customer_details')}</h3>
            </div>

            <div className="grid gap-x-6 gap-y-8 grid-cols-12 p-6">
                <div className="col-span-12 sm:col-span-6 lg:col-span-3">
                    <SelectInput name="customer" label={t('payment.create.customer_uuid')} id="customer"
                                 get={customerUuid}
                                 set={setCustomerUuid} disabled={!useExistingCustomer}>
                        <option value=""></option>
                        {customersList && customersList?.map((customer) => (
                            <option key={customer?.customerUuid}
                                    value={customer?.customerUuid}>{customer?.name}</option>))}
                    </SelectInput>
                </div>

                <div className="col-span-12 sm:col-span-6 lg:col-span-3">
                    <TextInput name="name" label={t('payment.create.name')} id="name" type="text" get={name}
                               set={setName} required={true}
                               disabled={useExistingCustomer}/>
                </div>

                <div className="col-span-12 sm:col-span-6 lg:col-span-3">
                    <TextInput name="email" label={t('payment.create.email')} id="email" type="email" get={email}
                               set={setEmail}
                               disabled={useExistingCustomer}/>
                </div>

                <div className="col-span-12 sm:col-span-6 lg:col-span-3">
                    <TextInput name="phone" label={t('payment.create.phone')} id="phone" type="text" get={phone}
                               set={setPhone}
                               disabled={useExistingCustomer}/>
                </div>


                <div className="col-span-12 sm:col-span-6 lg:col-span-3">
                    <TextInput name="address" label={t('payment.create.address')} id="address" type="text" get={address}
                               set={setAddress}
                               required={false}
                               disabled={useExistingCustomer}/>
                </div>


                <div className="col-span-12 sm:col-span-6 lg:col-span-3">
                    <TextInput name="city" label={t('payment.create.city')} id="city" type="text" get={city}
                               set={setCity} required={false}
                               disabled={useExistingCustomer}/>
                </div>


                <div className="col-span-12 sm:col-span-6 lg:col-span-3">
                    <TextInput name="zipCode" label={t('payment.create.zip_code')} id="zipCode" type="text"
                               get={zipCode} set={setZipCode}
                               required={false}
                               disabled={useExistingCustomer}/>
                </div>


                <div className="col-span-12 sm:col-span-6 lg:col-span-3">
                    <TextInput name="country" label={t('payment.create.country')} id="country" type="text" get={country}
                               set={setCountry}
                               required={false}
                               disabled={useExistingCustomer}/>
                </div>

                <div className="col-span-12 sm:col-span-6 lg:col-span-3">
                    <TextInput name="expires" label={t('payment.create.expires_at')} id="expires_at"
                               type="datetime-local" get={expiresAt}
                               set={setExpiresAt}
                               format="yyyy-MM-dd"
                               required={false}/>
                </div>


                {merchant?.allowSubVendors === true && (
                    <div className="col-span-12 sm:col-span-6 lg:col-span-3">
                        <SelectInput name="supplier" label={t('payment.create.supplier')} id="supplier" get={supplier}
                                     set={setSupplier}>
                            <option value=""></option>
                            {suppliersList?.map(supplier => <option key={supplier.supplierUuid}
                                                                    value={supplier.supplierUuid}>{supplier.supplierName}</option>)}
                        </SelectInput>
                    </div>
                )}


                <div className="col-span-12 sm:col-span-6 lg:col-span-3">
                    <TextInput name="customerReference" label={t('payment.create.customer_reference')}
                               id="customerReference" type="text" get={customerReference} set={setCustomerReference}
                               required={false}/>
                </div>

                {/* methaq prod M135728300300999 */}
                {/* samir dev,prod M862248712438901, M297763427187004 */}
                {['M135728300300999', 'M862248712438901', 'M297763427187004'].includes(merchant?.merchantUuid) &&
                    <div className="col-span-12 sm:col-span-6 lg:col-span-3">
                        <SelectInput name="brokerReference" label={t('payment.create.broker_reference')}
                                     id="brokerReference" get={brokerReference}
                                     set={setBrokerReference} required={true}>
                            <option value=""></option>
                            <option value="1200000208">LINKS INSURANCE BROKER LLC</option>
                            <option value="1200000203">MASTER INSURANCE BROKER LLC</option>
                            <option value="1200000209">SUCCESS INSURANCE SERVICES LLC</option>
                            <option value="1200000210">FIDELITY INSURANCE SERVICES LLC</option>
                            <option value="1200000201">ADVICE INSURANCE BROKER LLC</option>
                            <option value="1200000202">AL DIPLOMACY INSURANCE SERVICES LLC</option>
                            <option value="1200000205">NORTH STAR INSURANCE BROKER LLC</option>
                            <option value="1200000206">PROMISE INSURANCE SERVICES LLC</option>
                            <option value="1200000213">AL ITTIHAD AL DOWALI INSURANCE SERVICES LLC</option>
                            <option value="1200000211">TECHNICAL INSURANCE SERVICES LLC</option>
                            <option value="1200000207">HOSN AL SHARJAH INSURANCE SERVICES LLC</option>
                            <option value="1200000212">MIDDLE EAST BROKER LLC</option>
                            <option value="1200000204">NEW SHEILD INSURANCE BROKERS LLC</option>
                        </SelectInput>
                    </div>}

                {['M135728300300999', 'M862248712438901', 'M297763427187004'].includes(merchant?.merchantUuid) &&
                    <div className="col-span-12 sm:col-span-6 lg:col-span-3">
                        <SelectInput name="businessLine" label={t('payment.create.business_line')}
                                     id="businessLine" get={businessLine}
                                     set={setBusinessLine} required={true}>
                            <option value=""></option>
                            <option value="10 - Fire">10 - Fire</option>
                            <option value="20 - General Accident">20 - General Accident</option>
                            <option value="30 - Marine">30 - Marine</option>
                            <option value="40 - Motor">40 - Motor</option>
                            <option value="50 - Liability">50 - Liability</option>
                            <option value="60 - Engineering">60 - Engineering</option>
                            <option value="70 - Medical">70 - Medical</option>
                        </SelectInput>
                    </div>}

            </div>
        </div>

        <div className="bg-white rounded-xl border border-gray-200 shadow-sm pt-6">
            <div className="flex items-center justify-between gap-4">
                <h3 className="w-full text-lg font-medium text-white px-6 py-4 bg-gradient-to-br from-[#4296b5] to-[#95e0f2] rounded-t-xl -mt-6 border-b border-gray-200">{t('payment.create.products')}</h3>
            </div>
            <div className="p-6">
                {products.map((product) => (
                    <ProductFormItem key={product?.id} product={product} updateProduct={updateProduct}
                                     deleteProduct={deleteProduct}/>))}

                <div className="flex items-center justify-end mt-4">
                    <Button type="button" onClick={addProduct} secondary={false} className="rounded-full">
                        <span className="flex items-center gap-2"><PlusIcon
                            className="w-5 h-5"/>{t('payment.create.add_product')}</span>
                    </Button>
                </div>

                <div className="mt-4 flex items-center gap-6 flex-wrap">
                    {/*<Toggle get={splitPayment} set={value => setSplitPayment(value)} disabled={true}>Split Payment</Toggle>*/}
                    {/*<Toggle get={saveCustomerDetails} set={value => setSaveCustomerDetails(value)}>Save customer details</Toggle>*/}
                </div>

                <div className="mt-6 flex flex-wrap items-center gap-4">
                    <Button type="button" onClick={() => createPaymentMutation.mutate()}
                            disabled={createPaymentMutation.isPending}>
                        <span className="flex items-center gap-2">{t('payment.create.generate_link')}</span>
                    </Button>
                    <Button type="button" onClick={() => resetPaymentFormAction()} secondary={true}
                            className="!bg-gradient-to-br !from-gray-400 !to-gray-600 !border-gray-700">
                        <span className="flex items-center gap-2">{t('payment.create.reset_form')}</span>
                    </Button>
                    <MoneyHighlight
                        value={totalAmount}
                        currency={merchant?.currency}
                        className="!text-2xl !font-bold"
                    />
                    {/*<span*/}
                    {/*    className="inline-flex items-center rounded-full border border-cyan-200 bg-cyan-50 px-3 py-1 text-cyan-700 text-sm font-semibold">*/}
                    {/*    {t('payment.create.total_amount')}: {Number(totalAmount.toFixed(2))}*/}
                    {/*</span>*/}
                </div>

            </div>
        </div>
    </DashboardLayout>);
}

export default PaymentCreate;
